<script setup lang="ts">
    import { computed } from 'vue'
    import { useI18n } from 'vue-i18n'
    import {
        EVALUATION_MODEL,
        summarizeToolSelection,
        type ToolGroupKind,
        type UIChatMessage,
    } from 'models'

    const props = defineProps<{
        message: UIChatMessage
    }>()

    const { t: $t, n: $n } = useI18n({ useScope: 'global' })

    /**
     * The icon each kind has in the steps of the same detail (`getPartIcon`),
     * and for an API tool the one of its page, since its steps have none.
     */
    const KIND_ICONS: Record<ToolGroupKind, string> = {
        mcp: 'mcp',
        api: 'ri:plug-line',
        subagent: 'ri:robot-2-line',
    }

    /**
     * The working set's decision on this turn next to what the turn did with
     * it (spec tool-catalogue-working-set): shown where the message detail
     * lists the steps, since the evaluation is what comes before the first.
     */
    const summary = computed(() => summarizeToolSelection(props.message))

    /** The line of facts under the header: what each is goes in its label. */
    const facts = computed(() => {
        if (!summary.value) {
            return []
        }
        const { decision, groups } = summary.value
        return [
            {
                icon: 'ri:eye-line',
                label: $t('toolSelection.exposed', {
                    threshold: $n(decision.threshold, 'decimal'),
                }),
                value: `${groups.filter((group) => group.selected).length} / ${groups.length}`,
            },
            {
                icon: 'ri:hourglass-line',
                label: $t('toolSelection.latency', {
                    budget: $n(decision.budgetMs, 'integer'),
                }),
                value: `${$n(decision.latencyMs, 'integer')} ms`,
            },
            {
                icon: 'ri:wallet-line',
                // One significant digit on the line, the charge on hover.
                label:
                    decision.cost !== undefined
                        ? $t('toolSelection.cost', {
                              cost: $n(decision.cost, {
                                  key: 'currencyUSD',
                                  maximumSignificantDigits: 3,
                              }),
                          })
                        : $t('toolSelection.notCharged'),
                value:
                    decision.cost !== undefined
                        ? $n(decision.cost, {
                              key: 'currencyUSD',
                              maximumSignificantDigits: 1,
                          })
                        : '-',
            },
            {
                icon: 'ri:ai-generate-2-line',
                label: $t('toolSelection.inputTokens'),
                value:
                    decision.inputTokens !== undefined
                        ? `${$n(decision.inputTokens, 'integer')} ${$t('label.tokens')}`
                        : '-',
            },
        ]
    })
</script>

<template>
    <section v-if="summary" class="pk-tool-selection">
        <header
            class="text-12 text-word-2 leading-none flex items-center gap-12 bg-surface-1 rounded-t border-t border-x border-surface-3 p-8">
            <div
                class="flex items-center bg-gray-lighten-1 text-white px-6 py-3 rounded">
                <VvIcon name="ri:filter-3-line" class="w-12 h-12" />
            </div>
            <strong class="font-semibold text-12 truncate">
                {{ $t('toolSelection.title') }}
            </strong>
            <!-- A failure is a state to look into, the mode is not. -->
            <span
                v-if="summary.decision.failed"
                class="text-10 text-word-1 bg-surface-warning rounded px-4 py-2">
                {{ $t('toolSelection.failed') }}
            </span>
            <span
                v-else
                class="text-10 text-word-4 bg-surface-2 rounded px-4 py-2"
                :title="$t(`toolSelection.modeHint.${summary.decision.mode}`)">
                {{ $t(`toolSelection.mode.${summary.decision.mode}`) }}
            </span>
            <code class="font-mono text-10 text-word-3 truncate ml-auto">
                {{ EVALUATION_MODEL }}
            </code>
        </header>
        <div class="border rounded-b border-surface-3">
            <!-- Words only for what asks for a look: a failure, a miss. What
                 each mode does is on its badge, on hover. -->
            <div
                v-if="summary.decision.failed || summary.missed > 0"
                class="flex flex-col gap-6 p-8 border-b border-surface-3 text-12 text-word-2">
                <p v-if="summary.decision.failed">
                    {{
                        $t('toolSelection.leadFailed', {
                            budget: $n(summary.decision.budgetMs, 'integer'),
                        })
                    }}
                </p>
                <p v-if="summary.missed > 0" class="flex items-center gap-4">
                    <VvIcon
                        name="ri:error-warning-line"
                        class="shrink-0 text-warning-darken-3" />
                    {{
                        $t(
                            summary.decision.mode === 'active'
                                ? 'toolSelection.missedActive'
                                : 'toolSelection.missedShadow',
                            summary.missed,
                        )
                    }}
                </p>
            </div>
            <!-- One line, drawn like the facts at the top of the detail: an
                 icon for the label, which a screen reader still reads, and
                 the full wording on hover. -->
            <dl
                class="flex flex-wrap items-center justify-between gap-x-12 gap-y-4 p-8 border-b border-surface-3 text-12 text-word-3">
                <div
                    v-for="fact in facts"
                    :key="fact.icon"
                    class="flex items-center gap-6 min-w-0"
                    :title="fact.label">
                    <dt class="flex shrink-0">
                        <VvIcon :name="fact.icon" aria-hidden="true" />
                        <span class="pk-tool-selection__label">
                            {{ fact.label }}
                        </span>
                    </dt>
                    <dd class="tabular-nums truncate">
                        {{ fact.value }}
                    </dd>
                </div>
            </dl>
            <ul class="pk-tool-selection__groups">
                <li
                    v-for="group in summary.groups"
                    :key="group.id"
                    class="pk-tool-selection__group"
                    :class="{
                        'pk-tool-selection__group--selected': group.selected,
                        'pk-tool-selection__group--used': group.used,
                    }">
                    <VvIcon
                        :name="
                            group.selected
                                ? 'ri:check-line'
                                : 'ri:subtract-line'
                        "
                        class="pk-tool-selection__mark shrink-0" />
                    <!-- The kind as the icon before the name, the tags under
                         it: in a column of their own they left the name a
                         few letters. -->
                    <div class="flex flex-col gap-4 min-w-0">
                        <span class="flex items-center gap-6 min-w-0">
                            <VvIcon
                                :name="KIND_ICONS[group.kind]"
                                class="shrink-0 w-14 h-14"
                                :title="$t(`toolSelection.kind.${group.kind}`)"
                                aria-hidden="true" />
                            <span class="pk-tool-selection__label">
                                {{ $t(`toolSelection.kind.${group.kind}`) }}
                            </span>
                            <span
                                class="pk-tool-selection__name truncate"
                                :title="group.name">
                                {{ group.name }}
                            </span>
                        </span>
                        <span
                            class="flex flex-wrap items-center gap-x-6 gap-y-2 text-10 text-word-3">
                            <span v-if="group.kind === 'mcp'">
                                {{ $t('toolSelection.tools', group.tools) }}
                            </span>
                            <span
                                v-if="group.used && !group.selected"
                                class="text-word-1 bg-surface-warning rounded px-4 py-2">
                                {{ $t('toolSelection.missed') }}
                            </span>
                            <!-- On screen the row itself says it. -->
                            <span
                                v-if="group.used"
                                class="pk-tool-selection__label">
                                {{ $t('toolSelection.used') }}
                            </span>
                            <span
                                v-if="group.enabled"
                                class="pk-tool-selection__tag rounded px-4 py-2">
                                {{ $t('toolSelection.enabled') }}
                            </span>
                            <span
                                v-if="group.seeded"
                                class="pk-tool-selection__tag rounded px-4 py-2">
                                {{ $t('toolSelection.seeded') }}
                            </span>
                        </span>
                    </div>
                    <div
                        v-if="group.probability !== undefined"
                        class="pk-tool-selection__probability"
                        :title="
                            $t('toolSelection.probability', {
                                threshold: $n(
                                    summary.decision.threshold,
                                    'decimal',
                                ),
                            })
                        ">
                        <!-- A picture of the value printed next to it. -->
                        <span
                            class="pk-tool-selection__bar"
                            aria-hidden="true"
                            :style="{
                                '--pk-tool-selection-probability': `${group.probability * 100}%`,
                                '--pk-tool-selection-threshold': `${summary.decision.threshold * 100}%`,
                            }" />
                        <span class="text-10 text-word-2 tabular-nums">
                            {{ $n(group.probability, 'decimal') }}
                        </span>
                    </div>
                </li>
            </ul>
        </div>
    </section>
</template>

<style lang="scss">
    // Read by a screen reader, said another way on screen: the icon of a fact
    // or of a kind, the weight of a used row.
    .pk-tool-selection__label {
        position: absolute;
        width: 1px;
        height: 1px;
        overflow: hidden;
        clip-path: inset(50%);
        white-space: nowrap;
    }

    .pk-tool-selection__groups {
        max-height: 8rem;
        overflow-y: auto;
    }

    .pk-tool-selection__group {
        display: grid;
        grid-template-columns: auto minmax(0, 1fr) 6rem;
        align-items: center;
        gap: var(--spacing-sm);
        min-height: var(--spacing-40);
        padding: var(--spacing-6) var(--spacing-8);
        color: var(--color-word-3);

        & + & {
            border-top: var(--spacing-px) solid var(--color-surface-2);
        }
    }

    .pk-tool-selection__group--selected {
        color: var(--color-word-2);

        .pk-tool-selection__name {
            font-weight: var(--font-medium);
        }
    }

    // What the reply called, the fact the list is read for: one step of
    // surface and the heaviest weight, after the selected row so it wins.
    .pk-tool-selection__group--used {
        background-color: var(--color-surface-1);
        color: var(--color-word-1);

        .pk-tool-selection__name {
            font-weight: var(--font-semibold);
        }

        // One step deeper than the row, or the tag melts into it.
        .pk-tool-selection__tag {
            background-color: var(--color-surface-3);
        }
    }

    .pk-tool-selection__tag {
        background-color: var(--color-surface-2);
    }

    .pk-tool-selection__name {
        font-size: var(--text-12);
    }

    .pk-tool-selection__mark {
        width: var(--spacing-16);
        height: var(--spacing-16);
    }

    .pk-tool-selection__probability {
        display: flex;
        align-items: center;
        gap: var(--spacing-6);
    }

    .pk-tool-selection__bar {
        position: relative;
        flex: 1;
        height: var(--spacing-4);
        overflow: hidden;
        border-radius: var(--rounded-full);
        background-color: var(--color-surface-2);

        &::after {
            content: '';
            position: absolute;
            inset: 0 auto 0 0;
            width: var(--pk-tool-selection-probability, 0%);
            border-radius: inherit;
            background-color: currentColor;
        }

        // The exposure threshold: why a group on its left was left out.
        &::before {
            content: '';
            position: absolute;
            z-index: 1;
            inset: 0 auto 0 var(--pk-tool-selection-threshold, 0%);
            width: var(--spacing-px);
            background-color: var(--color-word-4);
        }
    }
</style>
